// ResetPassword.js
import React, { useState } from 'react';
import OTPInput from 'react-otp-input';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { Link } from 'react-router-dom';
import { KeyRound, CheckCircle2 } from 'lucide-react';
import API from '../services/api';
import AuthLayout from '../component/ui/AuthLayout';
import { inputClass } from '../component/ui/Field';
import Button from '../component/ui/Button';


const ForgotPassword = () => {
  const [email, setEmail] = useState('');
  const [otpSent, setOtpSent] = useState(false);
  const [otp, setOtp] = useState('');
  const [isVerified, setIsVerified] = useState(false);
  const [loading, setLoading] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  // Step 1: Handle requesting OTP
  const handleRequestOtp = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await API.post(`${process.env.REACT_APP_API_URL}/api/forgot-password`, { email });
      setOtpSent(true);
      toast.success('OTP has been sent to your email', {
        position: 'top-center',
        autoClose: 3000,
        theme: 'dark',
      });
    } catch (error) {
      toast.error(error.response?.data?.message || 'Something went wrong', {
        position: 'top-center',
        autoClose: 3000,
        theme: 'dark',
      });
    } finally {
      setLoading(false);
    }
  };

  // Step 2: Handle verifying OTP and resetting password
  const handleResetPassword = async (e) => {
    e.preventDefault();
    if (newPassword !== confirmPassword) {
      toast.error('Passwords do not match', {
        position: 'top-center',
        autoClose: 3000,
        theme: 'dark',
      });
      return;
    }
    setLoading(true);
    try {
      await API.post(`${process.env.REACT_APP_API_URL}/api/reset-password`, {
        email,
        otp,
        newPassword,
      });
      setIsVerified(true);
      toast.success('Password has been reset successfully', {
        position: 'top-center',
        autoClose: 3000,
        theme: 'dark',
      });
      // Optionally redirect to login page
    } catch (error) {
        console.log(error)
      toast.error(error.response?.data?.message || 'OTP verification failed', {
        position: 'top-center',
        autoClose: 3000,
        theme: 'dark',
      });
    } finally {
      setLoading(false);
    }
  };

  // Handle OTP input change
  const handleOTPChange = (otpValue) => {
    setOtp(otpValue);
  };

  return (
    <AuthLayout>
      {!otpSent ? (
        // Step 1: Request OTP
        <>
          <div className="flex items-center gap-2.5 mb-1">
            <span className="h-9 w-9 rounded-md bg-blue-50 text-blue-700 flex items-center justify-center">
              <KeyRound size={18} />
            </span>
            <div>
              <h1 className="text-xl font-semibold text-slate-900">Reset password</h1>
              <p className="text-xs text-slate-500">We'll email you a one-time code</p>
            </div>
          </div>
          <form onSubmit={handleRequestOtp} className="mt-6 space-y-4">
            <input
              type="email"
              placeholder="Enter your registered email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className={inputClass}
              required
            />
            <Button type="submit" variant="accent" size="lg" className="w-full" disabled={loading}>
              {loading ? (
                <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
                </svg>
              ) : (
                'Send OTP'
              )}
            </Button>
            <p className="text-center text-sm text-slate-500">
              Remembered it?{' '}
              <Link to="/login" className="text-blue-700 font-medium hover:underline">
                Back to sign in
              </Link>
            </p>
          </form>
        </>
      ) : !isVerified ? (
        // Step 2: Verify OTP and reset password
        <>
          <h1 className="text-xl font-semibold text-slate-900">Verify OTP</h1>
          <p className="text-sm text-slate-500 mt-1">
            An OTP has been sent to <strong className="text-slate-700">{email}</strong>
          </p>
          <form onSubmit={handleResetPassword} className="mt-6">
            <OTPInput
              value={otp}
              onChange={handleOTPChange}
              numInputs={6}
              renderInput={(props) => (
                <input
                  {...props}
                  placeholder="-"
                  className="w-12 h-12 border border-slate-300 bg-slate-50 rounded-md text-center text-lg focus:outline-none focus:ring-2 focus:ring-blue-600/40 focus:border-blue-600"
                />
              )}
              containerStyle={{
                display: 'flex',
                justifyContent: 'space-between',
                gap: '10px',
              }}
            />
            <input
              type="password"
              placeholder="New password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              className={`${inputClass} mt-4`}
              required
            />
            <input
              type="password"
              placeholder="Confirm new password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              className={`${inputClass} mt-3`}
              required
            />
            <Button type="submit" variant="accent" size="lg" className="w-full mt-4" disabled={loading}>
              {loading ? (
                <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
                </svg>
              ) : (
                'Reset password'
              )}
            </Button>
          </form>
        </>
      ) : (
        // Success message after password reset
        <div className="text-center">
          <div className="mx-auto h-12 w-12 rounded-full bg-emerald-50 text-emerald-600 flex items-center justify-center mb-4">
            <CheckCircle2 size={22} />
          </div>
          <h1 className="text-xl font-semibold text-slate-900">Password reset</h1>
          <p className="text-sm text-slate-500 mt-1">
            Your password has been reset successfully.
          </p>
          <Link
            to="/login"
            className="inline-flex items-center justify-center mt-6 px-4 py-2 bg-ink-900 text-white text-sm font-medium rounded-md hover:bg-ink-800 transition-colors"
          >
            Go to sign in
          </Link>
        </div>
      )}
    </AuthLayout>
  );
};

export default ForgotPassword;
